import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FirebaseError } from "firebase/app";
import { useAuth } from "../hooks/useAuth";
import { AppFooter } from "../components/AppFooter";
import { GoogleButton } from "../components/GoogleButton";
import { InstallHint } from "../components/InstallHint";
import { Sheet } from "../components/Sheet";

export function Login() {
  const { login, loginWithGoogle, resetPassword } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [googleError, setGoogleError] = useState<string | null>(null);

  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);
  const [resetSubmitting, setResetSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      navigate("/");
    } catch {
      setError("Anmeldung fehlgeschlagen. Bitte E-Mail und Passwort prüfen.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleGoogle() {
    setGoogleError(null);
    try {
      await loginWithGoogle();
    } catch {
      // A thrown/rejected promise here means the redirect never happened
      // (e.g. the domain isn't in Firebase's authorized-domains list, or the
      // provider isn't enabled) — without this, that failure was silent and
      // just looked like the button did nothing.
      setGoogleError("Google-Anmeldung fehlgeschlagen. Bitte erneut versuchen.");
    }
  }

  function openReset() {
    setResetEmail(email);
    setResetError(null);
    setResetSent(false);
    setResetOpen(true);
  }

  async function handleReset(e: FormEvent) {
    e.preventDefault();
    setResetError(null);
    setResetSubmitting(true);
    try {
      await resetPassword(resetEmail);
      // Shown regardless of whether the address is registered, so this
      // can't be used to check which emails have an account here.
      setResetSent(true);
    } catch (err) {
      if (err instanceof FirebaseError && err.code === "auth/invalid-email") {
        setResetError("Diese E-Mail-Adresse ist ungültig.");
      } else {
        setResetSent(true);
      }
    } finally {
      setResetSubmitting(false);
    }
  }

  return (
    <main className="min-h-dvh bg-cream-50 dark:bg-night-bg">
      <div className="hero-glow flex flex-col items-center justify-end px-6 pb-14 pt-20 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/25 backdrop-blur-sm">
          <svg viewBox="0 0 24 24" fill="currentColor" className="h-8 w-8 text-cream-50">
            <path d="M12 21s-7.5-4.6-10.3-9.3C.1 9.1 1 5.6 4 4.3c2.3-1 4.7-.1 6 1.8l2 2.9 2-2.9c1.3-1.9 3.7-2.8 6-1.8 3 1.3 3.9 4.8 2.3 7.4C19.5 16.4 12 21 12 21Z" />
          </svg>
        </div>
        <h1 className="mt-5 font-display text-3xl font-semibold tracking-tight text-cream-50">Wir</h1>
        <p className="mt-1.5 text-sm text-plum-100/80">Euer gemeinsamer Alltag, an einem Ort</p>
      </div>

      <div className="mx-auto -mt-8 max-w-md px-6 pb-12">
        <form onSubmit={handleSubmit} className="card animate-fade-up space-y-4 rounded-3xl p-6" noValidate>
          <div>
            <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-ink-900 dark:text-night-text">
              E-Mail
            </label>
            <input
              id="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-12 w-full rounded-xl border border-cream-200 bg-cream-50 px-4 text-base text-ink-900 outline-none transition-colors focus:border-plum-600 focus:bg-white dark:border-night-border dark:bg-night-surface-raised dark:text-night-text dark:focus:bg-night-surface-raised"
            />
          </div>
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <label htmlFor="password" className="block text-sm font-medium text-ink-900 dark:text-night-text">
                Passwort
              </label>
              <button type="button" onClick={openReset} className="text-xs font-medium text-plum-700 underline-offset-2 hover:underline dark:text-night-accent">
                Passwort vergessen?
              </button>
            </div>
            <input
              id="password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-12 w-full rounded-xl border border-cream-200 bg-cream-50 px-4 text-base text-ink-900 outline-none transition-colors focus:border-plum-600 focus:bg-white dark:border-night-border dark:bg-night-surface-raised dark:text-night-text dark:focus:bg-night-surface-raised"
            />
          </div>

          {error && (
            <p role="alert" className="rounded-lg bg-garnet-100 px-3 py-2 text-sm text-garnet-600 dark:bg-garnet-600/15">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="h-12 w-full rounded-xl bg-plum-700 text-base font-semibold text-cream-50 shadow-sm transition-all hover:bg-plum-800 active:scale-[0.985] disabled:opacity-60"
          >
            {submitting ? "Anmelden…" : "Anmelden"}
          </button>
        </form>

        <div className="mt-5 flex items-center gap-3 text-xs text-ink-400 dark:text-night-faint">
          <span className="h-px flex-1 bg-cream-200 dark:bg-night-border" />
          oder
          <span className="h-px flex-1 bg-cream-200 dark:bg-night-border" />
        </div>
        <div className="mt-3.5">
          <GoogleButton onClick={handleGoogle} />
        </div>
        {googleError && (
          <p role="alert" className="mt-3 rounded-lg bg-garnet-100 px-3 py-2 text-sm text-garnet-600 dark:bg-garnet-600/15">
            {googleError}
          </p>
        )}

        <Link
          to="/register"
          className="mt-6 block text-center text-sm font-medium text-plum-700 underline-offset-2 hover:underline dark:text-night-accent"
        >
          Noch kein Konto? Registrieren
        </Link>

        <InstallHint />

        <AppFooter />
      </div>

      <Sheet open={resetOpen} onClose={() => setResetOpen(false)} title="Passwort zurücksetzen">
        {resetSent ? (
          <div className="py-2 text-center">
            <p className="text-3xl">📩</p>
            <p className="mt-3 text-sm text-ink-600 dark:text-night-muted">
              Falls <span className="font-medium text-ink-900 dark:text-night-text">{resetEmail}</span> bei uns registriert ist, haben wir eine
              E-Mail mit einem Link zum Zurücksetzen gesendet.
            </p>
            <button
              onClick={() => setResetOpen(false)}
              className="mt-5 h-11 w-full rounded-xl bg-plum-700 text-sm font-semibold text-cream-50"
            >
              Schließen
            </button>
          </div>
        ) : (
          <form onSubmit={handleReset} className="space-y-3">
            <p className="text-sm text-ink-600 dark:text-night-muted">Gib deine E-Mail-Adresse ein, wir senden dir einen Link zum Zurücksetzen.</p>
            <input
              type="email"
              required
              autoComplete="email"
              placeholder="E-Mail"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              className="h-12 w-full rounded-xl border border-cream-200 bg-white px-4 text-base text-ink-900 outline-none focus:border-plum-600 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
            />
            {resetError && <p className="text-sm text-garnet-600">{resetError}</p>}
            <button
              type="submit"
              disabled={resetSubmitting}
              className="h-11 w-full rounded-xl bg-plum-700 text-sm font-semibold text-cream-50 disabled:opacity-50"
            >
              {resetSubmitting ? "Wird gesendet…" : "Link senden"}
            </button>
          </form>
        )}
      </Sheet>
    </main>
  );
}
